/*
  Product card image slider.
  - Desktop: appears on card hover, starting on the secondary image, with arrows and dots.
  - Mobile/touch: always visible, swipe to change image, dots only.
  Slides are kept in a <template> and only injected on first hover (desktop)
  or when the card scrolls near the viewport (mobile), so no extra images are
  requested up front.
*/
if (!customElements.get('product-hover-slider')) {
  class ProductHoverSlider extends HTMLElement {
    connectedCallback() {
      this.index = 0;
      this.count = parseInt(this.dataset.count, 10) || 0;
      this.card = this.closest('.grid-product__content') || this.parentElement;
      this.load = this.load.bind(this);

      if (ProductHoverSlider.desktop.matches) {
        this.card.addEventListener('mouseenter', this.load, { once: true });
        this.card.addEventListener('focusin', this.load, { once: true });
      } else {
        ProductHoverSlider.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.card.removeEventListener('mouseenter', this.load);
      this.card.removeEventListener('focusin', this.load);
      ProductHoverSlider.observer.unobserve(this);
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      ProductHoverSlider.observer.unobserve(this);
      const template = this.querySelector('template');
      if (!template) return;
      this.appendChild(template.content.cloneNode(true));
      template.remove();

      this.track = this.querySelector('.hover-slider__track');
      this.dots = this.querySelectorAll('.hover-slider__dot');

      // The card is wrapped in a link, so stop arrow/dot clicks (and the click
      // fired at the end of a swipe) from navigating.
      this.addEventListener('click', (e) => {
        if (this.swiped) {
          e.preventDefault();
          e.stopPropagation();
          this.swiped = false;
          return;
        }
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (btn.dataset.dir) this.goTo(this.index + parseInt(btn.dataset.dir, 10));
        else if (btn.dataset.index) this.goTo(parseInt(btn.dataset.index, 10));
      });

      this.addEventListener('touchstart', (e) => {
        this.startX = e.touches[0].clientX;
        this.startY = e.touches[0].clientY;
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        if (this.startX == null) return;
        const dx = e.changedTouches[0].clientX - this.startX;
        const dy = e.changedTouches[0].clientY - this.startY;
        this.startX = null;
        // Horizontal swipe only; vertical movement is page scroll.
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.swiped = true;
          this.goTo(this.index + (dx < 0 ? 1 : -1));
          setTimeout(() => { this.swiped = false; }, 400);
        }
      }, { passive: true });

      // Desktop: every hover opens on the secondary image (the primary is
      // already showing on the card). Reset on both enter and leave so a slide
      // picked during the last hover never carries over.
      const showSecondary = () => {
        if (ProductHoverSlider.desktop.matches) this.goTo(1, true);
      };
      showSecondary();
      ['mouseenter', 'mouseleave', 'pointerenter', 'pointerleave'].forEach((type) => {
        this.card.addEventListener(type, showSecondary);
      });
      this.classList.add('is-loaded');
    }

    goTo(i, instant = false) {
      if (!this.track) return;
      this.index = (i + this.count) % this.count;
      if (instant) {
        // Jump without the slide animation, then re-enable it after paint.
        this.track.classList.add('is-instant');
        cancelAnimationFrame(this.instantFrame);
        this.instantFrame = requestAnimationFrame(() => {
          this.instantFrame = requestAnimationFrame(() => this.track.classList.remove('is-instant'));
        });
      }
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.dots.forEach((dot, n) => dot.classList.toggle('is-active', n === this.index));
    }
  }

  ProductHoverSlider.desktop = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)');
  ProductHoverSlider.observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) entry.target.load();
    });
  }, { rootMargin: '200px 0px' });

  customElements.define('product-hover-slider', ProductHoverSlider);
}
